<script setup lang="ts">
import { computed } from 'vue'

import { ButtonComponent, FlexComponent, TypographyComponent } from '../../'
import RemoveIcon from '../../../assets/icons/remove.svg?component'
import { FILTER_CRITERION_CATEGORY_OPTIONS } from '../../../constants'
import {
  type AdvertiserGroup,
  type FilterCriterion,
  FilterCriterionCategoryEnum,
  type LoadAdvertisersForCriteria,
  type LoadClientsForCriteria,
  type LoadRevenueTiersForCriteria,
} from '../../../types'

import { AnalyticsFields, ClientFields, RevenueTierFields, SocialPlatformFields, UserFields, UtmFields } from './fields'

const props = withDefaults(
  defineProps<{
    row: FilterCriterion
    /** Each loader is absent where the host does not offer that criterion at all. */
    loadClients?: LoadClientsForCriteria
    loadRevenueTiers?: LoadRevenueTiersForCriteria
    loadAdvertisers?: LoadAdvertisersForCriteria
    hasAdvertiserSelect?: boolean
    advertiserGroup?: Pick<AdvertiserGroup, 'currency'> | null
    isDisabled?: boolean
    /** Why the row cannot be sent as it stands. Flags the row while set. */
    errorMessage?: string
  }>(),
  {
    loadClients: undefined,
    loadRevenueTiers: undefined,
    loadAdvertisers: undefined,
    hasAdvertiserSelect: true,
    advertiserGroup: null,
    isDisabled: false,
    errorMessage: undefined,
  },
)
const emits = defineEmits<{
  (e: 'update:row', row: FilterCriterion): void
  (e: 'remove'): void
}>()

const categoryLabel = computed(() => FILTER_CRITERION_CATEGORY_OPTIONS.find((option) => option.value === props.row.category)?.label ?? '')
const categorySubtitle = computed(() =>
  props.row.category === FilterCriterionCategoryEnum.Analytics ? "Excluding today's data." : undefined,
)

function narrowRow<C extends FilterCriterionCategoryEnum>(category: C) {
  return computed(() => (props.row.category === category ? (props.row as Extract<FilterCriterion, { category: C }>) : undefined))
}

const socialPlatformRow = narrowRow(FilterCriterionCategoryEnum.SocialPlatform)
const clientRow = narrowRow(FilterCriterionCategoryEnum.Client)
const revenueTierRow = narrowRow(FilterCriterionCategoryEnum.RevenueTier)
const analyticsRow = narrowRow(FilterCriterionCategoryEnum.Analytics)
const utmRow = narrowRow(FilterCriterionCategoryEnum.Utm)
const userRow = narrowRow(FilterCriterionCategoryEnum.User)

const handleFieldsUpdate = (row: FilterCriterion) => emits('update:row', row)
</script>

<template>
  <FlexComponent direction="column" :gap="4" :class="['filter-criterion-row', { 'filter-criterion-row--invalid': errorMessage }]">
    <FlexComponent align="center" :gap="8" class="filter-criterion-row__fields">
      <FlexComponent direction="column" :gap="4" class="filter-criterion-row__label">
        <TypographyComponent variant="title-large" :font-weight="600">{{ categoryLabel }}</TypographyComponent>
        <TypographyComponent v-if="categorySubtitle" variant="body-small" color="rgba(34, 34, 34, 0.48)">{{
          categorySubtitle
        }}</TypographyComponent>
      </FlexComponent>
      <FlexComponent flex="1" align="center" :gap="8" wrap="wrap">
        <SocialPlatformFields
          v-if="socialPlatformRow"
          :is-disabled="isDisabled"
          :row="socialPlatformRow"
          @update:row="handleFieldsUpdate"
        />
        <ClientFields
          v-else-if="clientRow"
          :is-disabled="isDisabled"
          :row="clientRow"
          :load-clients="loadClients"
          @update:row="handleFieldsUpdate"
        />
        <RevenueTierFields
          v-else-if="revenueTierRow"
          :is-disabled="isDisabled"
          :row="revenueTierRow"
          :load-revenue-tiers="loadRevenueTiers"
          @update:row="handleFieldsUpdate"
        />
        <AnalyticsFields
          v-else-if="analyticsRow"
          :is-disabled="isDisabled"
          :row="analyticsRow"
          :load-advertisers="loadAdvertisers"
          :has-advertiser-select="hasAdvertiserSelect"
          :advertiser-group="advertiserGroup"
          @update:row="handleFieldsUpdate"
        />
        <UtmFields v-else-if="utmRow" :is-disabled="isDisabled" :row="utmRow" @update:row="handleFieldsUpdate" />
        <UserFields v-else-if="userRow" :is-disabled="isDisabled" :row="userRow" @update:row="handleFieldsUpdate" />
      </FlexComponent>
      <ButtonComponent
        is-icon
        variant="transparent"
        :class="['filter-criterion-row__remove', { 'filter-criterion-row__remove--hidden': isDisabled }]"
        aria-label="Remove filter"
        @click="emits('remove')"
      >
        <RemoveIcon />
      </ButtonComponent>
    </FlexComponent>

    <TypographyComponent v-if="errorMessage" role="alert" variant="label-small" color="var(--error)">
      {{ errorMessage }}
    </TypographyComponent>
  </FlexComponent>
</template>

<style scoped lang="scss">
.filter-criterion-row {
  width: 100%;
  padding: 8px 8px 8px 16px;
  background: rgba(34, 34, 34, 0.04);
  border: 1px solid rgba(34, 34, 34, 0.08);
  border-radius: 8px;
}

.filter-criterion-row--invalid {
  border-color: var(--error);
}

.filter-criterion-row__fields {
  width: 100%;
}

.filter-criterion-row__label {
  flex-shrink: 0;
  min-width: 160px;
}

.filter-criterion-row__remove {
  flex-shrink: 0;
}

/*
 * Disabled rows keep the button's footprint so the fields stay aligned with the
 * editable rows above. `visibility: hidden` also drops it from the tab order and
 * the accessibility tree, so there is nothing left to click.
 */
.button.filter-criterion-row__remove--hidden {
  visibility: hidden;
}
</style>
